import React, { useState } from "react";
import { Link, useSearchParams } from "@/i18n/router";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { FileText, ArrowRight, Clock } from "lucide-react";
import { Seo } from "@/lib/seo";
import { PageHero } from "@/components/layout/PageHero";
import { Stagger, staggerItem } from "@/components/motion/Reveal";
import { PlatformCta } from "@/components/sections/PlatformCta";
import { useLang } from "@/i18n/LanguageContext";
import { api } from "@/lib/api";

export default function Publications() {
  const { tr } = useLang();
  const [params, setParams] = useSearchParams();
  const type = params.get("type") || "all";

  const { data } = useQuery({
    queryKey: ["publications", type],
    queryFn: async () => {
      const q = new URLSearchParams();
      if (type !== "all") q.set("pub_type", type);
      q.set("limit", "30");
      return (await api.get(`/publications?${q.toString()}`)).data;
    },
  });

  const items = data?.items || [];
  const types = data?.types || [];

  const setType = (t) => {
    const next = new URLSearchParams(params);
    if (t && t !== "all") next.set("type", t);
    else next.delete("type");
    setParams(next);
  };

  return (
    <>
      <Seo
        title={tr("White Paper & Publication Center")}
        description={tr("AstroLife™ white papers, technical notes, architecture papers, governance guides and position papers on verifiable AI governance and sovereign AI.")}
        path="/publications"
        keywords="AI Governance white papers, technical notes, architecture papers, governance guides, EU AI Act, sovereign AI"
        breadcrumbs={[{ label: tr("Publications") }]}
      />
      <PageHero
        overline={tr("Publication Center")}
        title={tr("White papers & institutional publications.")}
        subtitle={tr("Peer-quality publications on verifiable AI governance, European regulation and sovereign AI — available to read online and cite.")}
        breadcrumbs={[{ label: tr("Publications") }]}
      />

      <section className="bg-navy py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="flex flex-wrap gap-2 border-b border-navy-border pb-8">
            <button onClick={() => setType("all")} data-testid="pub-type-all" className={chip(type === "all")}>{tr("All")}</button>
            {types.map((t) => (
              <button key={t} onClick={() => setType(t)} data-testid={`pub-type-${t.replace(/\s+/g, "-").toLowerCase()}`} className={chip(type === t)}>{tr(t)}</button>
            ))}
          </div>

          {items.length === 0 ? (
            <div className="py-24 text-center text-silver/50" data-testid="pub-empty">{tr("No publications found.")}</div>
          ) : (
            <Stagger className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {items.map((p) => (
                <motion.article key={p.id} variants={staggerItem}>
                  <Link to={`/publications/${p.slug}`} data-testid={`pub-card-${p.slug}`} className="group flex h-full flex-col overflow-hidden rounded-2xl border border-navy-border bg-navy-surface transition-all duration-300 hover:-translate-y-1.5 hover:border-astro-blue/40">
                    <div className="aspect-[16/9] overflow-hidden">
                      <img src={p.cover_image} alt={p.title} loading="lazy" className="h-full w-full object-cover opacity-70 transition-transform duration-500 group-hover:scale-105" />
                    </div>
                    <div className="flex flex-1 flex-col p-6">
                      <div className="flex items-center gap-2 text-xs">
                        <span className="rounded-full bg-astro-blue/15 px-2.5 py-1 font-semibold text-astro-light">{tr(p.pub_type)}</span>
                        {p.reading_time && <span className="flex items-center gap-1 text-silver/40"><Clock className="h-3 w-3" /> {p.reading_time} min</span>}
                      </div>
                      <h2 className="mt-3 font-heading text-lg font-semibold leading-snug text-white group-hover:text-astro-light transition-colors">{p.title}</h2>
                      <p className="mt-2 line-clamp-3 flex-1 text-sm text-silver/60">{p.abstract}</p>
                      <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-astro-light">{tr("Read publication")} <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" /></span>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </Stagger>
          )}
        </div>
      </section>
      <PlatformCta />
    </>
  );
}

const chip = (active) => `rounded-full px-4 py-2 text-sm font-medium transition-colors ${active ? "bg-astro-blue text-white" : "border border-navy-border text-silver/70 hover:text-white"}`;
